import type { Island, Kitchen } from '@rta/rta-blueprint-library';

import { getCountertopContact, getDrawnBBox } from './get-countertop-contact';
import { getJointCountertopGroups } from './is-joint-countertop';

/**
 * SEAM markers for ADJOINING-island countertops on the blueprint countertop sheet.
 *
 * Whether two tops meet is decided by `getCountertopContact` — drawn slab geometry only, never
 * connection padding/overhang arithmetic.
 *
 * The seam IS the contact segment: the adjoining slab is not drawn on this slide, so its meeting
 * edge is drawn in its place as the red dashed line — spanning the slabs' overlap on the
 * non-separating axis (= the depth of whichever top's end terminates at the junction, identical on
 * both slides), nudged a small padding off the displayed slab, labelled with the segment length.
 */
export type AdjoiningSeamMarker = {
	id: string;
	x1: number;
	y1: number;
	x2: number;
	y2: number;
	/** Omitted for side-edge seams — those draw bare on the slab edge with no dimension. */
	label?: string;
};

export type GetAdjoiningCountertopSeamsParams = {
	island: Island;
	kitchen: Kitchen;
};

const MARKER_PADDING = 2;

const formatInches = (value: number): string => `${Math.round(value * 100) / 100}"`;

export const getAdjoiningCountertopSeams = (params: GetAdjoiningCountertopSeamsParams): Array<AdjoiningSeamMarker> => {
	const { island, kitchen } = params;
	const markers: Array<AdjoiningSeamMarker> = [];

	const [countertop] = kitchen.design.countertops.filterByIslandId(island.id);
	if (!countertop) return markers;
	const displayed = getDrawnBBox(countertop);
	if (!displayed) return markers;

	const ownJointGroup = getJointCountertopGroups(kitchen).find((group) => group.some((member) => member.id === island.id)) ?? null;

	kitchen.design.islands.children.forEach((neighbor) => {
		if (neighbor.id === island.id) return;

		const contact = getCountertopContact({ island, neighbor, kitchen, displayed });
		if (!contact) return;

		const { axis, start, end, other } = contact;
		const id = `${island.id}.seam.${neighbor.id}`;

		if (axis === 'x') {
			// Same joint-countertop group: one shared slab, one manually-placed seam — suppress the auto seam here.
			if (ownJointGroup?.some((member) => member.id === neighbor.id)) return;

			// Junction at the displayed slab's own SIDE edge (its end meets the other's face):
			// the seam draws bare, exactly ON the side edge line, full front-to-back — no
			// padding, no dimension. The labelled version lives on the other island's sheet.
			const x = other.cx > displayed.cx ? displayed.maxX : displayed.minX;
			markers.push({ id, x1: x, y1: displayed.minY, x2: x, y2: displayed.maxY });
			return;
		}

		// The other slab's end lands against the displayed front/back face: draw its
		// contact span nudged off the slab, with the seam dimension inline.
		const y = other.cy > displayed.cy ? displayed.maxY + MARKER_PADDING : displayed.minY - MARKER_PADDING;
		markers.push({ id, x1: start, y1: y, x2: end, y2: y, label: `${formatInches(end - start)} SEAM` });
	});

	return markers;
};
